import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ── React + Google Translate compatibility patch ─────────────
// Google Translate wraps text nodes in <font> tags to inject translations.
// This breaks React's DOM reconciliation, causing "Failed to execute
// 'removeChild' on 'Node'" crashes on translated pages.
// This patch makes Node.removeChild + insertBefore tolerant of Google's DOM
// mutations: instead of throwing, they no-op and the React tree continues.
// Ref: facebook/react #11538
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function patchedRemoveChild(child) {
    if (child && child.parentNode !== this) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[GTranslate patch] Cannot remove a child from a different parent', child);
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function patchedInsertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[GTranslate patch] Cannot insert before a reference node from a different parent', referenceNode);
      }
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// ── WINLUXOR — Force branding ─────────────────────────
// Lock page title (defends against any external override).
// Note: The actual title text is overridden by the branding system once settings load.
const setTitle = () => {
  const customTitle = window.__winluxor_title__;
  document.title = customTitle || 'WINLUXOR — Enter Your Dream';
};
setTitle();
setInterval(setTitle, 2500);

// Aggressively strip any 3rd-party badges / overlays / emergent traces
const stripExternalBadges = () => {
  const selectors = [
    '#emergent-badge',
    '[id*="emergent" i]',
    '[class*="emergent" i]',
    '[data-emergent-widget]',
    'iframe[src*="emergent" i]',
    'a[href*="emergent" i]',
    'button[class*="emergent" i]',
    'div[style*="z-index"][style*="fixed"][style*="bottom"][style*="right"]',
  ];
  selectors.forEach(sel => {
    try {
      document.querySelectorAll(sel).forEach(el => {
        if (el && el.id !== 'root' && !el.closest('#root')) el.remove();
      });
    } catch (_) {}
  });
};

stripExternalBadges();
setInterval(stripExternalBadges, 1000);

// MutationObserver — DO NOT REMOVE. Strips any badge injected dynamically.
const observer = new MutationObserver(() => stripExternalBadges());
observer.observe(document.documentElement, { childList: true, subtree: true });

// ── Disable Google Translate on admin pages ─────────────
// Admins don't need their dashboard translated — and translation triggers React
// crashes anyway. We mark admin pages with translate="no" so Google Translate
// (and Chrome's auto-translate) leaves the admin UI alone.
const applyAdminNoTranslate = () => {
  const isAdminPath = window.location.pathname.startsWith('/admin');
  if (isAdminPath) {
    document.documentElement.setAttribute('translate', 'no');
    document.documentElement.classList.add('notranslate');
    document.body?.setAttribute('translate', 'no');
    document.body?.classList.add('notranslate');
  } else {
    document.documentElement.removeAttribute('translate');
    document.documentElement.classList.remove('notranslate');
    document.body?.removeAttribute('translate');
    document.body?.classList.remove('notranslate');
  }
};
applyAdminNoTranslate();
// React Router uses pushState/replaceState — patch them so we re-apply on nav.
['pushState', 'replaceState'].forEach((m) => {
  const orig = window.history[m];
  window.history[m] = function () {
    const ret = orig.apply(this, arguments);
    setTimeout(applyAdminNoTranslate, 0);
    return ret;
  };
});
window.addEventListener('popstate', applyAdminNoTranslate);
